export const GUIDE_STATUSES = ["draft", "published", "unpublished"] as const;

export type GuideStatus = (typeof GUIDE_STATUSES)[number];

export function isGuideStatus(value: unknown): value is GuideStatus {
  return (
    typeof value === "string" &&
    GUIDE_STATUSES.some((status) => status === value)
  );
}

export interface GuideCity {
  cityId: string;
  name: string;
}

export interface GuideListCover {
  url: string;
  variants: {
    card: string;
    hero: string;
    thumb: string;
  } | null;
  alt: string;
  attributionState: "ok" | "incomplete";
}

export interface GuideListItem {
  id: string;
  title: string;
  city: GuideCity;
  status: GuideStatus;
  cover: GuideListCover | null;
  itineraryCount: number;
  placeCount: number;
  stalePlaces: number;
  updatedAt: string;
}

export interface GuideListPage {
  items: GuideListItem[];
  nextCursor: string | null;
}

export interface GuideListFilters {
  status?: GuideStatus;
  cityId?: string;
  stale?: true;
  q?: string;
}

export interface GuideListQuery extends GuideListFilters {
  limit?: number;
  cursor?: string;
}

export interface GuideStaleSummary {
  guides: number;
  places: number;
}

export interface GuideCityOption {
  cityId: string;
  name: string;
}

export const GUIDE_CATEGORIES = [
  "culture",
  "food",
  "coffee",
  "nature_beaches",
  "markets",
  "drinks",
  "neighbourhoods",
] as const;

export type GuideCategory = (typeof GUIDE_CATEGORIES)[number];

export const GUIDE_ROUTE_TYPES = ["days", "collection"] as const;

export type GuideRouteType = (typeof GUIDE_ROUTE_TYPES)[number];

export const GUIDE_ACTIVITY_TIMES = [
  "sunrise",
  "morning",
  "midday",
  "afternoon",
  "sunset",
  "evening",
  "night",
] as const;

export type GuideActivityTime = (typeof GUIDE_ACTIVITY_TIMES)[number];

export interface GuidePhoto {
  kind: "uploaded" | "external";
  url: string;
  variants: { card: string; hero: string; thumb: string } | null;
  alt: string;
  credit: string;
  license: string;
  sourceUrl: string;
  rightsStatus: "licensed" | "permission" | "editorial_free" | "unknown";
  host: string | null;
  attributionState: "ok" | "incomplete";
}

export interface GuideFactChip {
  label: string;
  value: string;
}

export interface GuideCategoryTile {
  category: GuideCategory;
  title: string;
  caption: string;
  photo: GuidePhoto | null;
}

export interface GuideNeighbourhood {
  id: string;
  name: string;
  caption: string;
  photo: GuidePhoto | null;
}

export interface GuideShortlistItem {
  entryId: string;
  tag: string | null;
}

export interface GuideFoodAndDrink {
  intro: string;
  entryIds: string[];
}

export interface GuideLocalExperience {
  entryId: string | null;
  title: string;
  caption: string;
}

export interface GuideTip {
  icon: string;
  title: string;
  text: string;
}

export interface GuideSectionsDraft {
  factChips: GuideFactChip[];
  atAGlance: string | null;
  categoryTiles: GuideCategoryTile[];
  neighbourhoods: GuideNeighbourhood[];
  shortlist: GuideShortlistItem[];
  foodAndDrink: GuideFoodAndDrink | null;
  localExperiences: GuideLocalExperience[];
  featuredRoutes: string[];
  knowBeforeYouGo: GuideTip[];
}

export interface GuidePlaceReference {
  entryId: string;
  name: string | null;
  seedName: string | null;
  status: "public" | "not_public" | "unavailable" | "unresolved";
  neighbourhoodId: string | null;
}

export interface GuideRouteReference {
  itineraryId: string;
  title: string;
}

export interface GuidePlace extends GuidePlaceReference {
  placeId: string | null;
  category: string | null;
  categoryKey: GuideCategory | null;
  district: string | null;
  note: string | null;
  photo: GuidePhoto | null;
  usedInItineraries: GuideRouteReference[];
}

export interface GuideItineraryStop {
  entryId: string;
  order: number;
  activityTime: GuideActivityTime | null;
}

export interface GuideItineraryDay {
  day: number;
  stops: GuideItineraryStop[];
}

export interface GuideItinerary extends GuideRouteReference {
  summary: string | null;
  routeType: GuideRouteType;
  order: number;
  days: GuideItineraryDay[];
  dayCount: number;
  placeCount: number;
  reviewed: { by: string; at: string } | null;
  updatedAt: string;
}

export interface GuideEditorSnapshot {
  id: string;
  revision: number;
  status: GuideStatus;
  title: string;
  summary: string;
  tagline: string | null;
  cityId: string;
  cityName: string;
  sections: GuideSectionsDraft;
  places: GuidePlace[];
  itineraries: GuideItinerary[];
}

export type GuideDraft = Omit<GuideEditorSnapshot, "id" | "revision" | "status"> & {
  id: string | null;
  revision: number | null;
  status: GuideStatus | null;
};

export type GuideWriteOutcome = "applied" | "pending" | "recorded";

export interface GuideWriteResult {
  guideId: string;
  revision: number;
  outcome: GuideWriteOutcome;
  replayed: boolean;
  commandId: string;
  details: Record<string, unknown>;
}
